import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCatalogDialog } from "@/hooks/use-catalog-dialog";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import { useForm } from "@/hooks/useForm";
import { z } from "zod";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useTranslation } from "react-i18next";

import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ErrorState, InfoRow, Text } from "@/components/ui/text";
import { Fab } from "@/components/ui/fab";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { FormLabel } from "@/components/ui/form-label";
import { HStack } from "@/components/ui/h-stack";
import { Icon } from "@/components/ui/icons";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { ModalBody, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { PageTitle } from "@/contexts/page-title-context";
import { Skeleton } from "@/components/ui/skeleton";
import { Surface } from "@/components/ui/surface";
import { FormGrid } from "@/components/ui/form-grid";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { VStack } from "@/components/ui/v-stack";
import { DividedList } from "@/components/ui/divided-list";
import { useSizeClass } from "@/hooks/use-size-class";
import { WizardStack } from "@/components/ui/wizard-stack";
import { WizardStepIndicator } from "@/components/ui/wizard-step-indicator";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { TableSkeleton } from "@/components/admin/table-skeleton";
import { DeletedIndicatorCell } from "@/components/admin/catalogs/deleted-indicator-cell";
import {
  createCountry,
  getCountries,
  updateCountry,
  type Country,
} from "@/lib/api/countries";
import { getCitiesOfCountry } from "@/lib/api/cities";
import { getRegions } from "@/lib/api/geo";
import {
  LocalizedNamesFields,
  toLocalizedName,
  type LocalizedNamesDraft,
} from "@/components/admin/catalogs/localized-names";
import { ConfirmActionButton } from "@/components/admin/catalogs/confirm-action-button";
import { DeletedWarningBanner } from "@/components/admin/catalogs/deleted-warning-banner";
import { useDeleteRestore } from "@/hooks/useDeleteRestore";
import { SelectableRow } from "@/components/ui/selectable-row";

type CountryRow = Country & { is_deleted?: boolean };

function VirtualizedCityList({
  cities,
}: {
  cities: { id: string; name: string; names?: { en?: string } }[];
}) {
  "use no memo";
  const parentRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual returns non-memoizable functions; "use no memo" already opts out of React Compiler
  const virtualizer = useVirtualizer({
    count: cities.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 15,
  });

  return (
    <Surface
      border="default"
      padding="none"
      className="max-h-64 overflow-auto divide-y divide-[color:var(--outline-variant)]"
      ref={parentRef}
    >
      <VStack
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const city = cities[virtualItem.index];
          return (
            <HStack
              key={city.id}
              className="absolute top-0 left-0 w-full px-3 py-2"
              style={{
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <Text weight="medium">{city.names?.en ?? city.name}</Text>
            </HStack>
          );
        })}
      </VStack>
    </Surface>
  );
}

function AdminCountriesCatalogPage() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const { t: tCatalogCountries } = useTranslation("admin.catalogs.countries");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const { offset } = usePageOffset();
  const {
    data,
    isLoading: loading,
    isError: loadError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.admin.countries({ limit: DEFAULT_PAGE_SIZE, offset }),
    queryFn: () => getCountries({ limit: DEFAULT_PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });
  const items = useMemo(() => (data?.data ?? []) as CountryRow[], [data?.data]);
  const total = data?.total ?? 0;
  const { page, totalPages, setPage } = usePagination({ total });
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const { filters: searchFilters, setFilter: setSearchFilter } =
    useFilterSearchParams({ keys: ["search", "country"] });
  const query = searchFilters.search;
  const setQuery = useCallback(
    (v: string) => setSearchFilter("search", v),
    [setSearchFilter],
  );
  const selectedCountryId = searchFilters.country;
  const setSelectedCountryId = useCallback(
    (v: string) => setSearchFilter("country", v),
    [setSearchFilter],
  );
  const { data: regionsData } = useQuery({
    queryKey: ["admin", "catalogs", "regions"],
    queryFn: () => getRegions(),
    staleTime: 60_000,
  });
  const regions = useMemo(() => regionsData ?? [], [regionsData]);
  const citiesQuery = useQuery({
    queryKey: ["admin", "catalogs", "countries", selectedCountryId, "cities"],
    queryFn: () => getCitiesOfCountry(selectedCountryId),
    enabled: Boolean(selectedCountryId),
    staleTime: 60_000,
  });

  const {
    isOpen: modalOpen,
    selectedItem: editing,
    openCreate: catalogOpenCreate,
    openEdit: catalogOpenEdit,
    close: catalogClose,
  } = useCatalogDialog<CountryRow>();
  const [wizardStep, setWizardStep] = useState(0);
  const countryForm = useForm({
    schema: z.object({
      en: z.string(),
      ru: z.string(),
      uz: z.string(),
      code: z.string(),
      flag: z.string(),
      dialCodes: z.string(),
      phoneFormat: z.string(),
    }),
    defaults: {
      en: "",
      ru: "",
      uz: "",
      code: "",
      flag: "",
      dialCodes: "",
      phoneFormat: "",
    },
    onSubmit: async () => {},
  });
  const namesDraft: LocalizedNamesDraft = {
    en: countryForm.fields.en,
    ru: countryForm.fields.ru,
    uz: countryForm.fields.uz,
  };
  const setNamesDraft = (draft: LocalizedNamesDraft) => {
    countryForm.setFields({ en: draft.en, ru: draft.ru, uz: draft.uz });
  };
  const code = countryForm.fields.code;
  const setCode = (v: string) => countryForm.setFields({ code: v });
  const flag = countryForm.fields.flag;
  const setFlag = (v: string) => countryForm.setFields({ flag: v });
  const dialCodes = countryForm.fields.dialCodes;
  const setDialCodes = (v: string) => countryForm.setFields({ dialCodes: v });
  const phoneFormat = countryForm.fields.phoneFormat;
  const setPhoneFormat = (v: string) =>
    countryForm.setFields({ phoneFormat: v });

  const { loading: deleteLoading, toggle: toggleDelete } =
    useDeleteRestore<CountryRow>({
      endpoint: "/countries",
      onSuccess: (_isDeleted, data) => {
        catalogOpenEdit(data as CountryRow);
        void queryClient.invalidateQueries({
          queryKey: ["admin", "catalogs", "countries"],
        });
      },
    });

  useEffect(() => {
    if (!selectedCountryId && items.length > 0) {
      setSelectedCountryId(items[0].id);
    }
  }, [items, selectedCountryId, setSelectedCountryId]);

  const selectedCountryCities = citiesQuery.data ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const name = (it.names?.en ?? it.name).toLowerCase();
      return (
        name.includes(q) ||
        (it.code ?? "").toLowerCase().includes(q) ||
        (it.emoji_flag ?? "").toLowerCase().includes(q) ||
        (it.dial_codes ?? []).join(",").toLowerCase().includes(q)
      );
    });
  }, [items, query]);

  const selectedCountry = useMemo(() => {
    return items.find((it) => it.id === selectedCountryId) ?? null;
  }, [items, selectedCountryId]);

  const selectedCountryRegions = useMemo(() => {
    if (!selectedCountryId) return [];
    return regions.filter((r) => r.country?.id === selectedCountryId);
  }, [regions, selectedCountryId]);

  function openCreate() {
    catalogOpenCreate();
    countryForm.reset({
      en: "",
      ru: "",
      uz: "",
      code: "",
      flag: "",
      dialCodes: "",
      phoneFormat: "",
    });
    setWizardStep(0);
  }

  function openEdit(item: CountryRow) {
    catalogOpenEdit(item);
    countryForm.reset({
      en: item.names?.en ?? item.name ?? "",
      ru: item.names?.ru ?? "",
      uz: item.names?.uz ?? "",
      code: item.code ?? "",
      flag: item.emoji_flag ?? "",
      dialCodes: (item.dial_codes ?? []).join(", "),
      phoneFormat: item.phone_format ?? "",
    });
    setWizardStep(0);
  }

  async function handleSave() {
    try {
      setSubmitting(true);
      setSaveError(false);

      const payload = {
        name: namesDraft.en,
        names: toLocalizedName(namesDraft),
        code: code.trim().toUpperCase(),
        emoji_flag: flag.trim(),
        dial_codes: dialCodes
          .split(",")
          .map((d) => d.trim())
          .filter(Boolean),
        phone_format: phoneFormat.trim(),
      };

      if (editing) {
        const updated = await updateCountry(editing.id, payload);
        setSelectedCountryId(updated.id);
      } else {
        const created = await createCountry(payload);
        setSelectedCountryId(created.id);
      }

      void queryClient.invalidateQueries({
        queryKey: ["admin", "catalogs", "countries"],
      });
      catalogClose();
    } catch (error) {
      console.error("Failed to save country", error);
      setSaveError(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <VStack gap="lg" data-testid="admin-catalog-countries-page">
      <VStack gap="none">
        <PageTitle>{tCatalogCountries("title")}</PageTitle>
      </VStack>
      <StickyHeaderSlot>
        <Card variant="bordered" padding="xs">
          <FilterToolbar
            searchPlaceholder={tCatalogCountries("searchPlaceholder")}
            searchValue={query}
            onSearchChange={setQuery}
            createAction={{
              label: tCatalogCountries("add"),
              onClick: openCreate,
            }}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
            data-testid="catalog-countries-toolbar"
          />
        </Card>
      </StickyHeaderSlot>

      {loadError || saveError ? (
        <ErrorState
          title={
            loadError
              ? tCatalogCountries("failedToLoad")
              : tCatalogCountries("failedToSave")
          }
          action={
            loadError ? (
              <Button
                color="neutral"
                variant="outline"
                onClick={() => void refetch()}
              >
                {tCommon("retry")}
              </Button>
            ) : undefined
          }
          className="py-10"
        />
      ) : null}

      <FormGrid cols="lg-3" gap="xl">
        <Card variant="bordered" padding="none" className="lg:col-span-1">
          <CardContent className="relative p-0">
            {loading && items.length > 0 && (
              <PageLoadingOverlay
                page={page}
                label={tCommon("pagination.loadingPage")}
              />
            )}
            {loading && items.length === 0 ? (
              <VStack gap="none" padding="md">
                <TableSkeleton
                  rows={6}
                  headers={["", tCommon("name"), tCommon("actions")]}
                />
              </VStack>
            ) : filtered.length === 0 ? (
              <VStack gap="none" padding="md">
                <Text color="muted">{tCatalogCountries("noItems")}</Text>
              </VStack>
            ) : (
              <DividedList rounded>
                {filtered.map((it) => {
                  const isDeleted = Boolean(it.is_deleted);
                  const mutedClass = isDeleted
                    ? "text-[color:var(--on-surface-variant)] opacity-70"
                    : "";
                  return (
                    <SelectableRow
                      key={it.id}
                      as="div"
                      selected={it.id === selectedCountryId}
                      onClick={() => setSelectedCountryId(it.id)}
                    >
                      <HStack justify="between" gap="lg">
                        <HStack truncate>
                          <DeletedIndicatorCell isDeleted={isDeleted} />
                          <Text
                            color="default"
                            weight="medium"
                            className={`truncate ${mutedClass}`}
                          >
                            {it.emoji_flag ? `${it.emoji_flag} ` : ""}
                            {it.names?.en ?? it.name}
                          </Text>
                        </HStack>
                        <Button
                          type="button"
                          color="neutral"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(it);
                          }}
                        >
                          {tCatalogCountries("edit")}
                        </Button>
                      </HStack>
                    </SelectableRow>
                  );
                })}
              </DividedList>
            )}
          </CardContent>
        </Card>

        <Card variant="bordered" padding="none" className="lg:col-span-2">
          <CardHeader>
            <HStack justify="between" gap="lg" className="w-full">
              <Text weight="medium">
                {selectedCountry?.names?.en ??
                  selectedCountry?.name ??
                  tCatalogCountries("title")}
              </Text>
              {selectedCountry ? (
                <Button
                  type="button"
                  color="neutral"
                  variant="outline"
                  onClick={() => openEdit(selectedCountry)}
                >
                  {tCatalogCountries("edit")}
                </Button>
              ) : null}
            </HStack>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <FormGrid>
                <InfoRow
                  label={tCatalogCountries("fields.code")}
                  value={<Skeleton className="h-4 w-20" />}
                />
                <InfoRow
                  label={tCatalogCountries("fields.flag")}
                  value={<Skeleton className="h-4 w-16" />}
                />
                <InfoRow
                  label={tCatalogCountries("fields.dialCodes")}
                  value={<Skeleton className="h-4 w-24" />}
                />
                <InfoRow
                  label={tCatalogCountries("fields.phoneFormat")}
                  value={<Skeleton className="h-4 w-24" />}
                />
              </FormGrid>
            ) : !selectedCountry ? (
              <Text color="muted">{tCatalogCountries("noItems")}</Text>
            ) : (
              <>
                <FormGrid>
                  <InfoRow
                    label={tCatalogCountries("fields.code")}
                    value={selectedCountry.code ?? "—"}
                  />
                  <InfoRow
                    label={tCatalogCountries("fields.flag")}
                    value={selectedCountry.emoji_flag ?? "—"}
                  />
                  <InfoRow
                    label={tCatalogCountries("fields.dialCodes")}
                    value={(selectedCountry.dial_codes ?? []).join(", ") || "—"}
                  />
                  <InfoRow
                    label={tCatalogCountries("fields.phoneFormat")}
                    value={selectedCountry.phone_format ?? "—"}
                  />
                </FormGrid>

                <VStack gap="xl" className="pt-2">
                  <VStack gap="sm">
                    <Text weight="medium">
                      {tCatalogCountries("sections.regions")} (
                      {selectedCountryRegions.length})
                    </Text>
                    {selectedCountryRegions.length === 0 ? (
                      <Text color="muted">{tCommon("noResults")}</Text>
                    ) : (
                      <FormGrid gap="md">
                        {selectedCountryRegions.map((region) => (
                          <Card key={region.id} variant="bordered" padding="sm">
                            <Text weight="medium">
                              {region.names?.en ?? region.name}
                            </Text>
                          </Card>
                        ))}
                      </FormGrid>
                    )}
                  </VStack>

                  <VStack gap="sm">
                    <Text weight="medium">
                      {tCatalogCountries("sections.cities")} (
                      {selectedCountryCities.length})
                    </Text>
                    {citiesQuery.isError ? (
                      <Text color="danger" weight="medium">
                        {tCommon("errorLoadingData")}
                      </Text>
                    ) : citiesQuery.isLoading ? (
                      <Surface border="default" rounded="lg" padding="md">
                        <VStack gap="sm">
                          {Array.from({ length: 4 }).map((_, index) => (
                            <Skeleton key={index} className="h-4 w-44" />
                          ))}
                        </VStack>
                      </Surface>
                    ) : selectedCountryCities.length === 0 ? (
                      <Text color="muted">{tCommon("noResults")}</Text>
                    ) : (
                      <VirtualizedCityList cities={selectedCountryCities} />
                    )}
                  </VStack>
                </VStack>
              </>
            )}
          </CardContent>
        </Card>
      </FormGrid>

      {isCompact && (
        <Fab
          onClick={openCreate}
          aria-label={tCatalogCountries("add")}
          data-testid="catalog-countries-fab"
        />
      )}

      <AdaptiveSheet
        open={modalOpen}
        onDismiss={() => {
          if (!submitting) catalogClose();
        }}
        onSubmit={() => void handleSave()}
        size="lg"
        data-testid="admin-country-modal"
      >
        {(() => {
          const useWizard = isCompact;
          const totalSteps = 2;
          const isLastStep = wizardStep >= totalSteps - 1;

          const namesContent = (
            <>
              <LocalizedNamesFields
                value={namesDraft}
                onChange={setNamesDraft}
                disabled={submitting}
                testIdPrefix="admin-country"
              />
              <FormGrid cols="2">
                <VStack gap="xs">
                  <FormLabel>{tCatalogCountries("fields.code")}</FormLabel>
                  <InputWrapper
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={submitting}
                    data-testid="admin-country-code"
                  />
                </VStack>
                <VStack gap="xs">
                  <FormLabel>{tCatalogCountries("fields.flag")}</FormLabel>
                  <InputWrapper
                    value={flag}
                    onChange={(e) => setFlag(e.target.value)}
                    disabled={submitting}
                    data-testid="admin-country-flag"
                  />
                </VStack>
              </FormGrid>
            </>
          );

          const phoneContent = (
            <VStack gap="lg">
              <VStack gap="xs">
                <FormLabel>{tCatalogCountries("fields.dialCodes")}</FormLabel>
                <InputWrapper
                  value={dialCodes}
                  onChange={(e) => setDialCodes(e.target.value)}
                  disabled={submitting}
                  data-testid="admin-country-dial-codes"
                />
              </VStack>
              <VStack gap="xs">
                <FormLabel>{tCatalogCountries("fields.phoneFormat")}</FormLabel>
                <InputWrapper
                  value={phoneFormat}
                  onChange={(e) => setPhoneFormat(e.target.value)}
                  disabled={submitting}
                  data-testid="admin-country-phone-format"
                />
              </VStack>
            </VStack>
          );

          const stepTitles = [
            tCommon("names"),
            tCatalogCountries("fields.phoneSettings"),
          ];

          return (
            <>
              {useWizard ? (
                <>
                  <HStack className="px-4 py-3">
                    {wizardStep > 0 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setWizardStep((s) => s - 1)}
                      >
                        <Icon.ArrowLeft size="sm" />
                      </Button>
                    ) : (
                      <HStack className="w-8" />
                    )}
                    <VStack align="center" className="flex-1">
                      <Text weight="semibold">
                        {editing
                          ? tCatalogCountries("modalEditTitle")
                          : tCatalogCountries("modalAddTitle")}
                      </Text>
                      <Text size="xs" color="muted">
                        {`${wizardStep + 1} / ${totalSteps} · ${stepTitles[wizardStep]}`}
                      </Text>
                    </VStack>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => catalogClose()}
                      disabled={submitting}
                    >
                      <Icon.X size="sm" />
                    </Button>
                  </HStack>
                  <WizardStepIndicator
                    totalSteps={totalSteps}
                    currentStep={wizardStep}
                  />
                </>
              ) : (
                <ModalHeader>
                  <Text weight="medium">
                    {editing
                      ? tCatalogCountries("modalEditTitle")
                      : tCatalogCountries("modalAddTitle")}
                  </Text>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => catalogClose()}
                    disabled={submitting}
                  >
                    <Icon.X size="sm" />
                  </Button>
                </ModalHeader>
              )}
              <ModalBody className="space-y-6">
                {editing && editing.is_deleted ? (
                  <DeletedWarningBanner />
                ) : null}
                {useWizard ? (
                  <WizardStack activeIndex={wizardStep}>
                    {namesContent}
                    {phoneContent}
                  </WizardStack>
                ) : (
                  <>
                    {namesContent}
                    {phoneContent}
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <HStack align="stretch" gap="lg">
                  {editing ? (
                    <ConfirmActionButton
                      isRestore={Boolean(editing.is_deleted)}
                      loading={deleteLoading}
                      disabled={submitting}
                      onConfirm={() =>
                        void toggleDelete(
                          editing.id,
                          Boolean(editing.is_deleted),
                        )
                      }
                      className="mr-3"
                    />
                  ) : null}
                  <Button
                    type="button"
                    color="neutral"
                    variant="outline"
                    onClick={() => catalogClose()}
                    disabled={submitting || deleteLoading}
                  >
                    {tCommon("cancel")}
                  </Button>
                  {useWizard && !isLastStep ? (
                    <Button
                      type="button"
                      onClick={() => setWizardStep((s) => s + 1)}
                    >
                      {tCommon("next")}
                      <Icon.ArrowRight size="xs" className="ml-1" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      loading={submitting}
                      disabled={
                        submitting ||
                        deleteLoading ||
                        !namesDraft.en.trim() ||
                        code.trim().length !== 2
                      }
                      onClick={() => void handleSave()}
                    >
                      {tCommon("save")}
                    </Button>
                  )}
                </HStack>
              </ModalFooter>
            </>
          );
        })()}
      </AdaptiveSheet>
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/catalogs/countries")({
  component: AdminCountriesCatalogPage,
});
